import { getCurrentInstance } from 'vue'
import { getActivePinia, type Pinia } from 'pinia'
import type { Country, CountryIso } from 'src/types/country'
import useContextStore from 'src/store/useContextStore'
import { FORMAT_PHONE_DATA_BY_COUNTRY } from 'src/functions/formatPhone/constants'
import type { PhoneData } from 'src/functions/formatPhone/types'

/**
 * Pinia текущего Vue-инстанса, без `getActivePinia()` в чужом корне.
 *
 * `getActivePinia()` при injection context сам делает `inject(pinia)` и
 * сыпет warn, если остров (`ClubCardInfo` и т.п.) смонтирован без Pinia.
 */
function getPiniaWithoutInject(): Pinia | undefined {
  const instance = getCurrentInstance()
  const instancePinia = (instance?.proxy as { $pinia?: Pinia } | undefined)?.$pinia

  if (instance) {
    return instancePinia
  }

  return getActivePinia() || undefined
}

function isNormalizePhoneCodeEnabled(): boolean {
  const pinia = getPiniaWithoutInject()

  if (!pinia) {
    return false
  }

  const { context } = useContextStore(pinia)

  return Boolean(context?.request?.feature_flags?.ff_lpu_branding_page_v3)
}

function getCountryPhoneCode(phoneCode?: string): string | null {
  if (!phoneCode) {
    return null
  }

  const digits = String(phoneCode).replace(/\D/g, '')

  return digits ? `+${digits}` : null
}

function getLegacyPhoneData(country?: Country): PhoneData | null {
  const rawPhoneCode = country?.phone_code
    ? `+${country.phone_code}`
    : null
  const countryPhoneCode = getCountryPhoneCode(country?.phone_code)

  const phoneDataByCode = rawPhoneCode
    ? Object.values(FORMAT_PHONE_DATA_BY_COUNTRY).find(item => item.code === rawPhoneCode) || null
    : null

  if (phoneDataByCode) {
    return {
      ...phoneDataByCode,
      code: countryPhoneCode || phoneDataByCode.code,
    }
  }

  const countryIso: CountryIso = country && country.iso
    ? country.iso.toLocaleLowerCase() as CountryIso
    : 'ru'
  const phoneData = countryIso && countryIso in FORMAT_PHONE_DATA_BY_COUNTRY
    ? FORMAT_PHONE_DATA_BY_COUNTRY[countryIso]
    : null

  return phoneData
    ? {
      ...phoneData,
      code: countryPhoneCode || phoneData.code,
    }
    : null
}

function getNormalizedPhoneData(country?: Country): PhoneData | null {
  const countryPhoneCode = getCountryPhoneCode(country?.phone_code)
  const countryIso = country?.iso
    ? country.iso.toLocaleLowerCase() as CountryIso
    : undefined
  const phoneDataByIso = countryIso && countryIso in FORMAT_PHONE_DATA_BY_COUNTRY
    ? FORMAT_PHONE_DATA_BY_COUNTRY[countryIso]
    : null

  if (phoneDataByIso) {
    return {
      ...phoneDataByIso,
      code: countryPhoneCode || phoneDataByIso.code,
    }
  }

  if (countryPhoneCode) {
    const phoneDataByCode = Object.values(FORMAT_PHONE_DATA_BY_COUNTRY)
      .find(item => item.code === countryPhoneCode)

    if (phoneDataByCode) {
      return phoneDataByCode
    }
  }

  if (!country?.iso) {
    return FORMAT_PHONE_DATA_BY_COUNTRY.ru
  }

  return null
}

/**
 * Данные маски и кода страны для форматирования телефона.
 *
 * `phone_code` из контекста может приходить как `7`, `+7` или `++7`.
 * Лишние плюсы снимаются, в номер попадает один `+`.
 */
export default function getPhoneData(
  country?: Country,
  normalizePhoneCode?: boolean,
): PhoneData | null {
  const useNormalized = normalizePhoneCode ?? isNormalizePhoneCodeEnabled()

  return useNormalized
    ? getNormalizedPhoneData(country)
    : getLegacyPhoneData(country)
}
